import log from '../logging.service';
import { isArray } from 'lodash';

declare global {
    interface Window {
        dictionary?: {[key: string]: string}
    }
}

declare type FormatArgs = string[];
declare type FormatNames = Record<string, string | number>;

class DictionaryService implements IDictionaryService {
    private dictionaryMap;

    constructor() {
        if (!window.dictionary) {
            throw new Error('window.dictionary should exist');
        }
        this.dictionaryMap = new Map<string, string>(Object.entries(window.dictionary));
    }

    public set(key: string, value: string) {
        const lowercaseKey = key.toLowerCase();
        if (!this.dictionaryMap.has(lowercaseKey)) {
            this.dictionaryMap.set(lowercaseKey, value);
        }
    }

    public get(key: string, args: FormatArgs = []) {
        const lowercaseKey = key ? key.toLowerCase() : key;

        const translated = this.dictionaryMap.get(lowercaseKey);
        if (translated === undefined) {
            log.error(`### Key '${key}' not found in dictionary ###`);
            return `##${key}`;
        }
        return this.format(translated, args);
    }

    public format(input: string, args: FormatArgs = []): string {
        return args.reduce((result, arg, ix) => {
            return result ? result.split(`{${ix}}`).join(arg) : arg;
        }, input);
    }

    public formatNamed(input: string, names: FormatNames = {}): string {
        let formatted = input || '';
        Object.keys(names).forEach(key => {
            // @ts-ignore
            formatted = formatted.replaceAll(`{${key}}`, names[key]);
        });
        return formatted;
    }

    public pluralize(count: number = 0, singularInput: string, pluralInput: string, argsOrNames: FormatArgs | FormatNames = []): string {
        const input = count === 1 ? singularInput : pluralInput;
        return isArray(argsOrNames) ? this.format(input, argsOrNames) : this.formatNamed(input, argsOrNames);
    }
}

const dictionaryService = new DictionaryService();

export default dictionaryService;

export interface IDictionaryService {
    get(key: string, args?: string[]): string;
    format(input: string, args: FormatArgs): string;
    formatNamed(input: string, names: FormatNames): string;
    pluralize(count: number, singularInput: string, pluralInput: string, argsOrNames?: FormatArgs | FormatNames): string;
}
